import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Building2, Users } from 'lucide-react';
import { Link } from 'react-router';
import { Card, CardBody, CardHeader } from '../../../components/ui/Card';
import { PageContainer, PageHeader } from '../../../components/ui/Page';
import { useAuth, useCan } from '../../auth/AuthProvider';
import { teamApi, teamKeys } from '../../team/api';
import { tenantApi, tenantKeys } from '../api';

function Stat({
  label,
  value,
  icon: Icon,
  to,
}: {
  label: string;
  value: string;
  icon: typeof Users;
  to?: string;
}) {
  const body = (
    <Card className="flex items-center gap-4 p-5">
      <span
        aria-hidden
        className="flex size-10 items-center justify-center rounded-lg bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200"
      >
        <Icon className="size-5" />
      </span>
      <div>
        <p className="text-2xl font-semibold tabular-nums text-fg">{value}</p>
        <p className="text-sm text-fg-muted">{label}</p>
      </div>
    </Card>
  );
  return to ? (
    <Link to={to} className="rounded-lg focus-visible:outline-2">
      {body}
    </Link>
  ) : (
    body
  );
}

/** Phase 2 dashboard: account health. Lead KPIs arrive with Phase 3/9 (docs/design/15 §3.2). */
export function DashboardPage() {
  const { me } = useAuth();
  const can = useCan();
  const staff = useQuery({
    queryKey: teamKeys.staff({ pageSize: 1 }),
    queryFn: () => teamApi.staff({ pageSize: 1 }),
    enabled: can('staff.view'),
  });
  const branches = useQuery({
    queryKey: teamKeys.branches,
    queryFn: () => teamApi.branches(),
    enabled: can('branches.view'),
  });
  const entitlements = useQuery({ queryKey: tenantKeys.entitlements, queryFn: tenantApi.entitlements });

  const limit = (key: string) => {
    const value = entitlements.data?.[key]?.limit;
    return value === null || value === undefined ? '' : ` / ${value}`;
  };

  return (
    <PageContainer>
      <PageHeader
        title={`Welcome, ${me?.user.fullName.split(' ')[0] ?? ''}`}
        description={me?.branding?.displayName ?? me?.tenant?.name}
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {can('staff.view') ? (
          <Stat
            label="Staff members"
            icon={Users}
            to="/app/team/staff"
            value={staff.data ? `${staff.data.meta.total ?? 0}${limit('limit.staff')}` : '…'}
          />
        ) : null}
        {can('branches.view') ? (
          <Stat
            label="Branches"
            icon={Building2}
            to="/app/team/branches"
            value={
              branches.data
                ? `${branches.data.data.filter((b) => b.status === 'active').length}${limit('limit.branches')}`
                : '…'
            }
          />
        ) : null}
      </div>
      <Card className="mt-6">
        <CardHeader
          title="Coming next"
          description="Lead capture, assignment and follow-ups arrive in the next phases."
        />
        <CardBody className="text-sm text-fg-muted">
          <ul className="list-inside list-disc space-y-1">
            <li>Phase 3 — leads, pipelines, assignment and transfer</li>
            <li>Phase 4 — the staff mobile app</li>
            <li>Phase 5 — high-priority assignment alerts</li>
          </ul>
          {can('tenant.configure') ? (
            <Link
              to="/app/settings/business"
              className="mt-4 inline-flex items-center gap-1 font-medium text-brand-700 hover:underline dark:text-brand-300"
            >
              Review business settings <ArrowRight aria-hidden className="size-4" />
            </Link>
          ) : null}
        </CardBody>
      </Card>
    </PageContainer>
  );
}
